import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Save, 
  ArrowLeft, 
  User, 
  Mail, 
  Phone, 
  Building, 
  Globe, 
  MapPin,
  FileText,
  Tag,
  DollarSign
} from 'lucide-react';
import { leadsAPI, organizationsAPI } from '../services/api';
import toast from 'react-hot-toast';
import { formatPhoneNumber } from '../utils/phoneFormatter';
import CustomFieldsInput from '../components/CustomFieldsInput';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const LeadForm = () => {
  const { user: currentUser } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEditing = Boolean(id);
  const [customFields, setCustomFields] = useState({});
  const { data: leadCustomFieldDefs = [] } = useQuery(
    ['custom-fields', 'lead'],
    () => api.get('/custom-fields', { params: { entity: 'lead' } }).then((r) => r.data),
    { staleTime: 60 * 1000 }
  );

  // Lead verilerini çek (düzenleme modunda)
  const { data: lead, isLoading: isLoadingLead } = useQuery(
    ['lead', id],
    () => leadsAPI.getLead(id),
    {
      enabled: isEditing,
      refetchOnWindowFocus: false,
      staleTime: 0
    }
  );

  const { data: orgUsersResp } = useQuery(
    ['organization', 'users'],
    () => organizationsAPI.getUsers(),
    { staleTime: 60_000 }
  );
  const orgUsers = orgUsersResp?.data?.users || [];

  const { data: stagesData } = useQuery(
    ['pipeline-stages'],
    async () => {
      const pipelines = await api.get('/pipelines').then((r) => r.data);
      if (!pipelines?.[0]) return [];
      return api.get(`/pipelines/${pipelines[0]._id}/stages`).then((r) => r.data);
    },
    { staleTime: 60_000 }
  );
  const stages = stagesData || [];

  const { register, handleSubmit, formState: { errors }, reset, watch, setValue } = useForm({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      company: '',
      companyEmail: '',
      position: '',
      website: '',
      companyPhone: '',
      address: {
        street: '',
        city: '',
        state: '',
        zipCode: '',
        country: ''
      },

      status: 'new',
      stageId: '',
      source: 'other',
      priority: 'medium',
      tags: '',
      assignedTo: '',
      estimatedValue: '',
      currency: 'USD'
    }
  });

  // Debug için form değerlerini izle
  const formValues = watch();

  // Lead verilerini form'a doldur
  useEffect(() => {
    if (isEditing && lead) {
      // API response'u kontrol et
      const leadData = lead.data || lead;
      
      const formData = {
        firstName: leadData.firstName || '',
        lastName: leadData.lastName || '',
        email: leadData.email || '',
        phone: leadData.phone || '',
        company: leadData.company || '',
        companyEmail: leadData.companyEmail || '',
        position: leadData.position || '',
        website: leadData.website || '',
        companyPhone: leadData.companyPhone || '',
        address: {
          street: leadData.address?.street || '',
          city: leadData.address?.city || '',
          state: leadData.address?.state || '',
          zipCode: leadData.address?.zipCode || '',
          country: leadData.address?.country || ''
        },

        status: leadData.status || 'new',
        stageId: leadData.stageId || '',
        source: leadData.source || 'other',
        priority: leadData.priority || 'medium',
        tags: leadData.tags ? leadData.tags.join(', ') : '',
        assignedTo: leadData.assignedTo || '',
        estimatedValue: leadData.estimatedValue ? leadData.estimatedValue.toString() : '',
        currency: leadData.currency || 'USD'
      };
      reset(formData);

      const cf = leadData.customFields;
      if (cf && typeof cf === 'object') {
        setCustomFields(cf instanceof Map ? Object.fromEntries(cf) : { ...cf });
      }
    }
  }, [lead, isEditing, reset]);

  const createMutation = useMutation(leadsAPI.createLead, {
    onSuccess: (data) => {
      toast.success('Lead created successfully!');
      queryClient.invalidateQueries(['leads']);
      queryClient.invalidateQueries('dashboardStats');
      navigate('/leads');
    },
    onError: (error) => {
      console.error('Create lead error:', error);
      console.error('Error response data:', error.response?.data);
      const errorData = error.response?.data;
      
      // Duplicate kontrol hatası için özel mesajlar
      if (errorData?.field) {
        const fieldName = errorData.field === 'email' ? 'email address' : 'phone number';
        const existingLead = errorData.existingLead;
        
        let message = `This ${fieldName} is already registered`;
        if (existingLead) {
          message += `\n\nExisting record: ${existingLead.fullName}`;
          if (existingLead.company) {
            message += ` (${existingLead.company})`;
          }
        }
        
        toast.error(message, {
          duration: 5000,
          style: {
            background: '#000000',
            border: '1px solid #333333',
            color: '#ffffff'
          }
        });
      } else {
        const message = errorData?.error || 'An error occurred while creating the lead';
        toast.error(message);
      }
    }
  });

  const updateMutation = useMutation(
    (data) => leadsAPI.updateLead(id, data),
    {
      onSuccess: (data) => {
        toast.success('Lead updated successfully!');
        queryClient.invalidateQueries(['leads']);
        queryClient.invalidateQueries(['lead', id]);
        queryClient.invalidateQueries('dashboardStats');
        navigate(`/leads/${id}`);
      },
      onError: (error) => {
        const errorData = error.response?.data;
        
        // Duplicate kontrol hatası için özel mesajlar
        if (errorData?.field) {
          const fieldName = errorData.field === 'email' ? 'email address' : 'phone number';
          const existingLead = errorData.existingLead;
          
          let message = `This ${fieldName} is already used by another lead`;
          if (existingLead) {
            message += `\n\nExisting record: ${existingLead.fullName}`;
            if (existingLead.company) {
              message += ` (${existingLead.company})`;
            }
          }
          
          toast.error(message, {
            duration: 5000,
            style: {
              background: '#000000',
              border: '1px solid #333333',
              color: '#ffffff'
            }
          });
        } else {
          const message = errorData?.error || 'An error occurred while updating the lead';
          toast.error(message);
        }
      }
    }
  );

  const onSubmit = (data) => {
    // Tags'ı array'e çevir
    if (data.tags) {
      data.tags = data.tags.split(',').map(tag => tag.trim()).filter(tag => tag);
    }

    // Estimated value'yu number'a çevir
    if (data.estimatedValue) {
      data.estimatedValue = parseFloat(data.estimatedValue);
    }

    if (Object.keys(customFields).length > 0) {
      data.customFields = customFields;
    }

    if (isEditing) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  // Loading durumu
  if (isEditing && isLoadingLead) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner"></div>
        <span className="ml-2 text-gray-600">Loading lead...</span>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            {isEditing ? 'Edit Lead' : 'New Lead'}
          </h1>
          <p className="mt-2 text-gray-600">
            {isEditing ? 'Update lead information' : 'Add a new lead'}
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex space-x-3">
          <button
            onClick={() => navigate(-1)}
            className="btn-secondary"
          >
            <ArrowLeft size={16} className="mr-2" />
            Back
          </button>
          <button
            onClick={handleSubmit(onSubmit)}
            disabled={createMutation.isLoading || updateMutation.isLoading || (isEditing && isLoadingLead)}
            className="btn-primary"
          >
            <Save size={16} className="mr-2" />
            {isEditing ? 'Update' : 'Save'}
          </button>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Left Column */}
          <div className="space-y-6">
            {/* Personal Information */}
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-medium text-gray-900 flex items-center">
                  <User size={20} className="mr-2" />
                  Personal Information
                </h3>
              </div>
              <div className="card-body space-y-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      First Name *
                    </label>
                    <input
                      type="text"
                      {...register('firstName', { required: 'First name is required' })}
                      className={errors.firstName ? 'input-error' : 'input'}
                      placeholder="First name"
                    />
                    {errors.firstName && (
                      <p className="mt-1 text-sm text-danger-600">{errors.firstName.message}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Last Name
                    </label>
                    <input
                      type="text"
                      {...register('lastName')}
                      className="input"
                      placeholder="Last name"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    {...register('email', { 
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Please enter a valid email address'
                      }
                    })}
                    className={errors.email ? 'input-error' : 'input'}
                    placeholder="example@email.com"
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-danger-600">{errors.email.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone
                  </label>
                  <input
                    type="tel"
                    {...register('phone')}
                    className={errors.phone ? 'input-error' : 'input'}
                    placeholder="8184567588"
                    onBlur={(e) => {
                      if (e.target.value) {
                        const formatted = formatPhoneNumber(e.target.value);
                        e.target.value = formatted;
                      }
                    }}
                  />
                  {errors.phone && (
                    <p className="mt-1 text-sm text-danger-600">{errors.phone.message}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Company Information */}
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-medium text-gray-900 flex items-center">
                  <Building size={20} className="mr-2" />
                  Company Information
                </h3>
              </div>
              <div className="card-body space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Company Name
                  </label>
                  <input
                    type="text"
                    {...register('company')}
                    className={errors.company ? 'input-error' : 'input'}
                    placeholder="Company name"
                  />
                  {errors.company && (
                    <p className="mt-1 text-sm text-danger-600">{errors.company.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Company Email
                  </label>
                  <input
                    type="email"
                    {...register('companyEmail', {
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Please enter a valid email address'
                      }
                    })}
                    className={errors.companyEmail ? 'input-error' : 'input'}
                    placeholder="info@company.com"
                  />
                  {errors.companyEmail && (
                    <p className="mt-1 text-sm text-danger-600">{errors.companyEmail.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Position
                  </label>
                  <input
                    type="text"
                    {...register('position')}
                    className={errors.position ? 'input-error' : 'input'}
                    placeholder="Position"
                  />
                  {errors.position && (
                    <p className="mt-1 text-sm text-danger-600">{errors.position.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Website
                  </label>
                  <input
                    type="url"
                    {...register('website')}
                    className={errors.website ? 'input-error' : 'input'}
                    placeholder="https://www.example.com"
                  />
                  {errors.website && (
                    <p className="mt-1 text-sm text-danger-600">{errors.website.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Company Phone
                  </label>
                  <input
                    type="tel"
                    {...register('companyPhone')}
                    className={errors.companyPhone ? 'input-error' : 'input'}
                    placeholder="Company phone number"
                    onBlur={(e) => {
                      if (e.target.value) {
                        const formatted = formatPhoneNumber(e.target.value);
                        e.target.value = formatted;
                      }
                    }}
                  />
                  {errors.companyPhone && (
                    <p className="mt-1 text-sm text-danger-600">{errors.companyPhone.message}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Lead Details */}
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-medium text-gray-900 flex items-center">
                  <Tag size={20} className="mr-2" />
                  Lead Details
                </h3>
              </div>
              <div className="card-body space-y-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    {/* Controlled select — stages list is async, so an
                        uncontrolled `register('stageId')` snaps to the
                        first option when the dropdown renders before the
                        stages query resolves. Watching + setValue keeps
                        the lead's actual stage selected. */}
                    <select
                      value={watch('stageId') || ''}
                      onChange={(e) =>
                        setValue('stageId', e.target.value, { shouldDirty: true })
                      }
                      className="input"
                      disabled={stages.length === 0}
                    >
                      {stages.length === 0 && <option value="">Loading…</option>}
                      {stages.map((s) => (
                        <option key={s._id} value={s._id}>{s.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Priority
                    </label>
                    <select
                      {...register('priority')}
                      className="input"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Source
                  </label>
                  <select
                    {...register('source')}
                    className="input"
                  >
                    <option value="website">Website</option>
                    <option value="phone">Phone</option>
                    <option value="phone_agent">Phone Agent</option>
                    <option value="email">Email</option>
                    <option value="social_media">Social Media</option>
                    <option value="business_card">Business Card</option>
                    <option value="referral">Referral</option>
                    <option value="google_ads">Google Ads</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                {currentUser?.permissions?.assign_lead && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Assigned To
                    </label>
                    <select {...register('assignedTo')} className="input">
                      <option value="">— Unassigned —</option>
                      {orgUsers.map((u) => (
                        <option key={u._id} value={u._id}>
                          {u.name || u.email}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Estimated Value
                    </label>
                    <input
                      type="number"
                      {...register('estimatedValue')}
                      className="input"
                      placeholder="0"
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Currency
                    </label>
                    <select
                      {...register('currency')}
                      className="input"
                    >
                      <option value="USD">USD</option>
                      <option value="TRY">TRY</option>
                      <option value="EUR">EUR</option>
                      <option value="GBP">GBP</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tags
                  </label>
                  <input
                    type="text"
                    {...register('tags')}
                    className="input"
                    placeholder="Tag1, Tag2, Tag3"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Separate tags with commas
                  </p>
                </div>
              </div>
            </div>

            {/* Address */}
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-medium text-gray-900 flex items-center">
                  <MapPin size={20} className="mr-2" />
                  Address Information
                </h3>
              </div>
              <div className="card-body space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Street Address
                  </label>
                  <input
                    type="text"
                    {...register('address.street')}
                    className="input"
                    placeholder="Street address"
                  />
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      City
                    </label>
                    <input
                      type="text"
                      {...register('address.city')}
                      className="input"
                      placeholder="City"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      State/Province
                    </label>
                    <input
                      type="text"
                      {...register('address.state')}
                      className="input"
                      placeholder="State/Province"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Zip Code
                    </label>
                    <input
                      type="text"
                      {...register('address.zipCode')}
                      className="input"
                      placeholder="Zip code"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Country
                    </label>
                    <input
                      type="text"
                      {...register('address.country')}
                      className="input"
                      placeholder="Country"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Custom Fields (hidden until admin defines any) */}
        {leadCustomFieldDefs.length > 0 && (
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-medium text-gray-900 flex items-center">
                <Tag size={20} className="mr-2" />
                Custom Information
              </h3>
            </div>
            <div className="card-body">
              <CustomFieldsInput entity="lead" values={customFields} onChange={setCustomFields} />
            </div>
          </div>
        )}

      </form>
    </div>
  );
};

export default LeadForm; 